import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { IoAddCircleOutline } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import ReactDatePicker from 'react-datepicker';
import CreatableSelect from 'react-select/creatable';
import { datePickerValue, smartSum } from '../../../utilities/common';

const AddCustomFactor = ({
  onSubmit,
  usrDateFormat,
  requireFields,
  gasFields,
  isLoading = false,
  unitOptions = [],
  getSelectedUnit,
}) => {
  const { t } = useTranslation();
  const initialData = {
    fromDate: null,
    toDate: null,
    unit: null,
    co2eFactor: '',
    energyContentFactor: '',
    co2Factor: '',
    ch4Factor: '',
    n2oFactor: '',
    hfcFactor: '',
    sf6Factor: '',
    pfcFactor: '',
    nf3Factor: '',
  };

  const [formData, setFormData] = useState(initialData);
  const [touched, setTouched] = useState({});

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      if (field === 'co2eFactor') {
        gasFields.forEach((f) => {
          newData[f] = '';
        });
      }
      if (gasFields.includes(field)) {
        const numbers = gasFields.map((key) => {
          const val = key === field ? value : prev[key];
          if (!val) {
            return '';
          }
          return val && Number(val) ? Number(val) : 0;
        });
        if (numbers.every(i => i === '')) {
          newData.co2eFactor = '';
        } else {
          newData.co2eFactor = String(smartSum(numbers.filter(i => i !== '')));
        }
      }
      return newData;
    });

    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const isInvalid = (field, required = false, ignoreTouched = false) => {
    const value = formData[field];
    if (!touched[field] && !ignoreTouched) return false;
    if (['fromDate', 'toDate', 'unit'].includes(field)) {
      return required && !value;
    }
    if (!value && required) {
      return true;
    }
    if (value !== '' && (isNaN(parseFloat(value)) || !isFinite(value))) {
      return true;
    }
    return false;
  };

  const handleAdd = async () => {
    const payload = {
      fromDate: formData.fromDate,
      toDate: formData.toDate,
      unit: formData.unit,
      co2eFactor: formData.co2eFactor,
      energyContentFactor: formData.energyContentFactor,
      co2Factor: formData.co2Factor || null,
      ch4Factor: formData.ch4Factor || null,
      n2oFactor: formData.n2oFactor || null,
      hfcFactor: formData.hfcFactor || null,
      sf6Factor: formData.sf6Factor || null,
      pfcFactor: formData.pfcFactor || null,
      nf3Factor: formData.nf3Factor || null,
    };
    try {
      await onSubmit(payload);
      setFormData(initialData);
      setTouched({});
    } catch (e) {
      // ignore log
    }
  };

  const renderNumberField = (field, label, required = false) => (
    <td>
      <Form.Group className="mb-0">
        <Form.Control
          type="text"
          value={formData[field]}
          onChange={(e) => handleChange(field, e.target.value)}
          isInvalid={isInvalid(field, required)}
        />
        <Form.Control.Feedback type="invalid">
          {t('{{title}} must be valid number.', { title: label })}
        </Form.Control.Feedback>
      </Form.Group>
    </td>
  );

  return (
    <tfoot>
      <tr>
        {/* From Date */}
        <td>
          <Form.Group className="mb-0" style={{ minWidth: '150px' }}>
            <ReactDatePicker
              isClearable
              dateFormat={usrDateFormat}
              peekNextMonth
              showMonthDropdown
              showYearDropdown
              className={`form-control ${isInvalid('fromDate', true) ? 'is-invalid' : ''}`}
              {...datePickerValue(formData.fromDate, (val) => handleChange('fromDate', val))}
            />
            {isInvalid('fromDate', true) && (
              <div className="invalid-feedback d-block">
                {t('The from date is required')}
              </div>
            )}
          </Form.Group>
        </td>

        {/* To Date */}
        <td>
          <Form.Group className="mb-0" style={{ minWidth: '150px' }}>
            <ReactDatePicker
              isClearable
              dateFormat={usrDateFormat}
              peekNextMonth
              showMonthDropdown
              showYearDropdown
              className={`form-control ${isInvalid('toDate', true) ? 'is-invalid' : ''}`}
              {...datePickerValue(formData.toDate, (val) => handleChange('toDate', val))}
            />
            {isInvalid('toDate', true) && (
              <div className="invalid-feedback d-block">
                {t('The to date is required')}
              </div>
            )}
          </Form.Group>
        </td>

        {/* Unit */}
        <td>
          <Form.Group className="mb-0" style={{ minWidth: '200px' }}>
            <CreatableSelect
              value={formData.unit ? getSelectedUnit(formData.unit) : null}
              onChange={(opt) => handleChange('unit', opt.value)}
              options={unitOptions}
              placeholder={t('Select Unit')}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                control: (base) => ({
                  ...base,
                  borderColor: isInvalid('unit', true) ? '#dc3545' : base.borderColor,
                  boxShadow: isInvalid('unit', true) ? '0 0 0 0.2rem rgba(220,53,69,.25)' : base.boxShadow
                })
              }}
            />
            {isInvalid('unit', true) && (
              <div className="invalid-feedback d-block">
                {t('The unit is required')}
              </div>
            )}
          </Form.Group>
        </td>

        {/* Numeric Fields */}
        {renderNumberField('co2eFactor', t('Emission Factor in CO2-e (t)'), true)}
        {renderNumberField('energyContentFactor', t('Energy Content Factor (TJ)'), true)}
        {renderNumberField('co2Factor', t('CO2 Conversion Factor'), false)}
        {renderNumberField('ch4Factor', t('CH4 Conversion Factor'), false)}
        {renderNumberField('n2oFactor', t('N2O Conversion Factor'), false)}
        {renderNumberField('hfcFactor', t('HFC Conversion Factor'), false)}
        {renderNumberField('sf6Factor', t('SF6 Conversion Factor'), false)}
        {renderNumberField('pfcFactor', t('PFC Conversion Factor'), false)}
        {renderNumberField('nf3Factor', t('NF3 Conversion Factor'), false)}

        {/* Action Button */}
        <td>
          <Button
            variant="dark"
            className="table-add-button"
            onClick={handleAdd}
            disabled={isLoading || !!Object.keys(initialData).find((field) => isInvalid(field, requireFields.includes(field), true))}
          >
            <IoAddCircleOutline />
          </Button>
        </td>
      </tr>
    </tfoot>
  );
};

export default AddCustomFactor;
